import { useRef, useMemo, useState, memo } from "react";

import styles from "./styles.module.scss";
import { DEFAULT_AVATAR_URL, UPLOAD_BASE_URL } from "utils/constants";
import userDefaultLogo from "assets/logos/user-default-logo.svg";
import WithLink from "components/WithLink";
import { AttachmentOutlinedComponent } from "assets/icons";
import Modal from "../../Modal";
import ModalBody from "../../Modal/ModalBody";
import ModalFooter from "../../Modal/ModalFooter";
import useTranslation from "hooks/useTranslation";
import { getNotifMsg } from "utils/message";
import { toast } from "react-toastify";
import { isValidFileToUploadAsImage } from "utils/validation";

const UserAvatar = ({
  avatar,
  acceptedFormats,
  renderProgress,
  showProgress,
  handleUploadAvatar,
}) => {
  const [hasAvatarError, setHasAvatarError] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { getTranslation, getAppLocale } = useTranslation(["CHAT", "COMMON"]);
  const { t } = getTranslation();
  const locale = getAppLocale();
  const fileInputRef = useRef(null);

  const isAvatarUploadedByOperator = useMemo(
    () => /^[0-9a-fA-F]{24}$/.test(avatar),
    [avatar]
  );

  const isChannelDefaultPhoto = avatar?.includes("channel-photos");

  const userAvatarAddress = useMemo(
    () => {
      if (!avatar) return "";

      // channel avatar
      if (isChannelDefaultPhoto) return userDefaultLogo;

      // file uploaded by operator
      if (isAvatarUploadedByOperator)
        return `${UPLOAD_BASE_URL}/${avatar}?size=small`;

      // others(avatar uploaded by widget API, ...)
      return avatar;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [avatar]
  );

  const avatarPreviewLink =
    !isChannelDefaultPhoto && !hasAvatarError ? userAvatarAddress : "";

  const handleFileChange = (event) => {
    const fileObj = event.target.files[0];
    if (!fileObj) {
      return;
    }

    if (!isValidFileToUploadAsImage(fileObj.type)) {
      toast.error(getNotifMsg("INVALID_FILE_FORMAT", locale));
      return;
    }

    setUploadingFile(fileObj);
    setShowModal(true);

    event.target.value = null;
  };

  const renderPreviewModal = (
    <Modal
      setVisibility={setShowModal}
      visible={showModal}
      className="confirmation-modal"
    >
      <ModalBody>
        <div>
          <img
            src={uploadingFile && URL.createObjectURL(uploadingFile)}
            alt="avatar"
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="modal__container__footer__left">
          <button
            className="btn btn-tertiary"
            id="closeModal"
            onClick={() => {
              setShowModal(false);
            }}
          >
            {t("CANCEL")}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              handleUploadAvatar(uploadingFile);
              setShowModal(false);
            }}
          >
            {t("CONFIRM")}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );

  return (
    <>
      {renderPreviewModal}
      <div
        data-has-effect={!isChannelDefaultPhoto}
        data-show-border={!showProgress && isAvatarUploadedByOperator}
        className={styles.user_avatar}
      >
        {showProgress ? (
          renderProgress
        ) : (
          <WithLink className={styles.wrapper_link} link={avatarPreviewLink}>
            <img
              src={userAvatarAddress}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null; // prevents looping
                currentTarget.src = DEFAULT_AVATAR_URL;
                setHasAvatarError(true);
              }}
              alt="user-avatar"
            />
          </WithLink>
        )}

        {!showProgress && handleUploadAvatar && (
          <div className={styles.slider}>
            <div
              className={styles.slider_attachment_icon}
              onClick={
                showProgress ? undefined : () => fileInputRef.current.click()
              }
            >
              <AttachmentOutlinedComponent />
            </div>
          </div>
        )}

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={acceptedFormats}
        />
      </div>
    </>
  );
};

UserAvatar.defaultProps = {
  avatar: "",
  acceptedFormats: "image/gif, image/jpeg, image/png",
  showProgress: false,
};

export default memo(UserAvatar);
